import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate('/journey', { replace: true });
    }
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-16 bg-[#F2F0EB]">
        <div>
          <h1 className="text-2xl text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Calm Scale™
          </h1>
        </div>
        
        <div className="max-w-md">
          <p className="text-4xl leading-tight text-[#2C2C2C] mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            Your 14-day blueprint to a launched digital product.
          </p>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#6B8E7C]/10 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-sm text-[#6B8E7C] font-medium">1</span>
              </div>
              <div>
                <p className="font-medium text-[#2C2C2C]">Clarity</p>
                <p className="text-sm text-[#2C2C2C]/60">Define your vision and ideal client</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#6B8E7C]/10 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-sm text-[#6B8E7C] font-medium">2</span>
              </div>
              <div>
                <p className="font-medium text-[#2C2C2C]">Assets</p>
                <p className="text-sm text-[#2C2C2C]/60">Build your lead magnets and content</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#6B8E7C]/10 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-sm text-[#6B8E7C] font-medium">3</span>
              </div>
              <div>
                <p className="font-medium text-[#2C2C2C]">Authority</p>
                <p className="text-sm text-[#2C2C2C]/60">Establish your credibility</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#6B8E7C]/10 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-sm text-[#6B8E7C] font-medium">4</span>
              </div>
              <div>
                <p className="font-medium text-[#2C2C2C]">Launch</p>
                <p className="text-sm text-[#2C2C2C]/60">Go live without the burnout</p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-[#2C2C2C]/40">
          Strategic clarity for sustainable growth.
        </p>
      </div>

      {/* Right Side - Login */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-12 text-center">
            <h1 className="text-2xl text-[#2C2C2C] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Calm Scale™
            </h1>
            <p className="text-sm text-[#2C2C2C]/60">The 14-Day Blueprint</p>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Welcome
            </h2>
            <p className="text-[#2C2C2C]/60 font-light">
              Sign in to continue your journey.
            </p>
          </div>

          <Button
            onClick={login}
            className="w-full py-6 bg-white border border-[#E5E0D6] text-[#2C2C2C] hover:bg-[#F2F0EB] rounded-xl flex items-center justify-center gap-3 transition-colors"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-xs text-[#2C2C2C]/40 mt-8">
            By continuing, you agree to our terms of service.
          </p>

          <div className="mt-16 pt-8 border-t border-[#E5E0D6] text-center">
            <p className="text-sm text-[#2C2C2C]/50 italic">
              "Take your time. Clarity compounds."
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
